
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useBilling } from '@/hooks/billing/useBilling';
import { useAdminStatus } from '@/hooks/admin/useAdminStatus';
import BillingNavbar from '@/components/billing/BillingNavbar';
import { format } from 'date-fns';
import { FileText } from 'lucide-react';
import { generateReceiptPDF } from '@/utils/pdfGenerator';

const ReceiptsPage: React.FC = () => {
  const { receipts, isLoading } = useBilling();
  const { isAdmin } = useAdminStatus();

  const handleGeneratePDF = (receipt: any) => {
    generateReceiptPDF(receipt);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading receipts...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BillingNavbar />
      
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Receipts</h1>
          <p className="text-gray-600">
            {isAdmin 
              ? 'View all payment receipts and transaction history'
              : 'Your payment receipts and transaction history'
            }
          </p>
        </div>

        {receipts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No receipts found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {receipts.map((receipt) => (
              <Card key={receipt.id}>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Receipt #{receipt.id.slice(-8)}
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Payment Date: {format(new Date(receipt.payment_date), 'MMM d, yyyy')}
                  </p>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    <div className="text-2xl font-bold text-green-600">
                      ${receipt.payment_amount.toFixed(2)}
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      Invoice: #{receipt.invoice_id.slice(-8)}
                    </div>

                    {receipt.wave_payment_id && (
                      <div className="text-sm text-gray-600">
                        Payment ID: {receipt.wave_payment_id}
                      </div>
                    )}

                    <Button
                      variant="outline"
                      onClick={() => handleGeneratePDF(receipt)}
                      className="w-full flex items-center"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Download Receipt
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptsPage;
